/**
 * Which treatment phase's educational content a hub step speaks in.
 *
 * Split out of `MoonHubGuidanceCarousel` so it can be asserted. It could not be
 * before: the carousel is a component module, and the flows that declare the
 * step ids live in another one, so nothing could check that every step actually
 * has an entry here. Two did not, and both showed a patient the wrong thing —
 * `outcome` explained baseline bloodwork before stimulation on a tab headed
 * "Outcome", and `pgt_testing` offered help with the medication protocol.
 *
 * A missing entry is never a visible no-op: `getMoonHubStepPhase` falls back to
 * `getting_started`, which is real, confident, and wrong.
 */
import type { TreatmentPhaseName } from "@louise/care/treatment-phases";

export const STEP_TO_PHASE: Record<string, TreatmentPhaseName> = {
  pre_treatment: "getting_started" as TreatmentPhaseName,
  stimulation: "stimulation" as TreatmentPhaseName,
  protocol: "stimulation" as TreatmentPhaseName,
  trigger_shot: "trigger" as TreatmentPhaseName,
  egg_retrieval: "retrieval" as TreatmentPhaseName,
  freezing: "retrieval" as TreatmentPhaseName,
  fertilization: "fertilization" as TreatmentPhaseName,
  embryo_development: "fertilization" as TreatmentPhaseName,
  transfer: "transfer" as TreatmentPhaseName,
  luteal_phase: "waiting" as TreatmentPhaseName,
  // The wait and the result. Unmapped, this fell back to `getting_started` and
  // the Outcome tab explained baseline bloodwork before stimulation — and told a
  // partner to stay out of hot tubs.
  outcome: "waiting" as TreatmentPhaseName,
  sperm_collection: "retrieval" as TreatmentPhaseName,
  insemination: "retrieval" as TreatmentPhaseName,
  screening: "getting_started" as TreatmentPhaseName,
  analysis: "fertilization" as TreatmentPhaseName,
  // Pre-existing gap of the same shape: the `pgt` step block supplies no
  // `when_to_call`, so that card fell through to the phase and offered help with
  // the medication protocol on a genetic-testing step.
  pgt_testing: "fertilization" as TreatmentPhaseName,
};

export function getMoonHubStepPhase(stepId: string): TreatmentPhaseName {
  return STEP_TO_PHASE[stepId] ?? ("getting_started" as TreatmentPhaseName);
}
